import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import useTranslation from '../../../hooks/useTranslation';
import useEscapeKey from '../../../hooks/useEscapeKey';
import { formatCurrency } from '../../../utils/formatCurrency';
import { supabase } from '../../../lib/supabase';
import paymentRequestService from '../../../services/paymentRequestService';
import { Checkbox } from '../../../components/ui/Checkbox';
import { computeDpTotals } from '../../../utils/dpFinancials';

const DEFAULT_LINE = () => ({
  id: null,
  description: '',
  contractAmount: 0,
  isExtra: false,
  // Une ligne de credit porte un contractAmount NEGATIF. Le signe vit dans
  // project_extras.type, jamais dans le montant stocke : on le pose une seule
  // fois, a la selection, et il descend jusqu'a payment_items.contract_amount.
  isCredit: false,
  previousAmount: 0,
  currentAmount: 0,
  monthlyAmount: 0,
  percentInput: '',
  dollarInput: '',
  errors: {},
  linkedExtraId: null,
  isEditingContractAmount: false,
  contractRawInput: '',
});

const STATUS_OPTIONS = ['draft', 'submitted', 'approved'];

const STATUS_COLORS = {
  draft: 'text-gray-500',
  submitted: 'text-blue-600',
  approved: 'text-green-600',
};

const STATUS_ITEM_COLORS = {
  draft: 'text-gray-500',
  submitted: 'text-blue-600',
  approved: 'text-green-600',
};

// ─── Format percentage: remove trailing zeros, max 2 decimals ───────────────
const formatPercentage = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  // toFixed(2) then remove trailing zeros
  return parseFloat(num.toFixed(2)).toString();
};

// ─── Plancher a zero ────────────────────────────────────────────────────────
// Une ligne de contrat et une ligne d'extra refusent le negatif, exactement
// comme avant. SEULE une ligne de credit peut descendre sous zero : c'est
// toute sa raison d'etre.
const floorAtZero = (line, value) => (line?.isCredit ? value : Math.max(0, value));

const AmountInput = ({ value, onChange = undefined, onFocus = undefined, onBlur = undefined, readOnly = false, hasError = false, placeholder = '0.00', className = '' }) => (
  <div className="relative">
    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none select-none">$</span>
    <input
      type="text"
      value={value}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      readOnly={readOnly}
      placeholder={placeholder}
      className={`w-full h-9 pl-5 pr-1 text-xs text-right font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 overflow-hidden ${readOnly ? 'bg-gray-50 text-gray-500' : ''} ${hasError ? 'border-red-400' : 'border-gray-200'} ${className}`}
    />
  </div>
);

// ─── Period date helpers ─────────────────────────────────────────────────────
const getDefaultPeriodStart = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
};

const getDefaultPeriodEnd = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  return new Date(year, month + 1, 0).toISOString().slice(0, 10);
};

const PaymentRequestModal = ({
  isOpen,
  onClose,
  onSave,
  projectId,
  requestId,
  requestNumber,
  existingStatus,
}) => {
  const { t } = useTranslation();
  const isDP1 = requestNumber === 1 || requestNumber == null;
  const isEditMode = !!requestId;

  const [baseLines, setBaseLines] = useState([DEFAULT_LINE()]);
  const [extraLines, setExtraLines] = useState([]);
  const [status, setStatus] = useState(existingStatus || 'draft');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [statusOpen, setStatusOpen] = useState(false);
  const statusRef = useRef(null);

  // ─── Holdback state ──────────────────────────────────────────────────────────
  const [applyHoldback, setApplyHoldback] = useState(true);

  // ─── Period state ────────────────────────────────────────────────────────────
  const [periodStart, setPeriodStart] = useState(getDefaultPeriodStart());
  const [periodEnd, setPeriodEnd] = useState(getDefaultPeriodEnd());
  const [periodError, setPeriodError] = useState('');

  const [approvedExtras, setApprovedExtras] = useState([]);
  const [usedExtraIds, setUsedExtraIds] = useState(new Set());
  const deletedItemIds = useRef(new Set());

  const [docFiles, setDocFiles] = useState([]);
  const [loadingDocFiles, setLoadingDocFiles] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);
  const fileInputRef = useRef(null);

  const storagePath = requestId ? `payment_request/${requestId}` : null;

  // Close status dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (statusRef?.current && !statusRef.current.contains(e.target)) setStatusOpen(false);
    };
    if (statusOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [statusOpen]);

  const loadApprovedExtras = useCallback(async () => {
    if (!projectId) return;
    try {
      const { data, error } = await supabase
        ?.from('project_extras')
        ?.select('id, title, cost_accepted, amount, type, category, status, co_number')
        ?.eq('project_id', projectId)
        ?.eq('status', 'approved')
        // Les erreurs internes sont absorbees par LHIAM : jamais facturees.
        // 🔴 .neq() seul ELIMINERAIT les categories NULL — en SQL, NULL <> 'x'
        // vaut NULL, pas true, et PostgREST ne garde que les true. Une
        // vingtaine d'extras ont category = NULL et disparaitraient.
        ?.or('category.is.null,category.neq.internal_error');
      if (!error) setApprovedExtras(data || []);
    } catch (err) {
      console.error('Error loading approved extras:', err);
    }
  }, [projectId]);

  const loadUsedExtraIds = useCallback(async () => {
    if (!projectId) return;
    try {
      const { data, error } = await supabase
        ?.from('payment_items')
        ?.select('linked_extra_id')
        ?.eq('project_id', projectId)
        ?.not('linked_extra_id', 'is', null);
      if (!error) {
        const ids = new Set((data || []).map(item => item?.linked_extra_id));
        setUsedExtraIds(ids);
      }
    } catch (err) {
      console.error('Error loading used extra IDs:', err);
    }
  }, [projectId]);

  // ─── Load existing data ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    deletedItemIds.current = new Set();
    loadApprovedExtras();
    loadUsedExtraIds();
    if (isEditMode && requestId && requestNumber) {
      loadData();
      loadPeriodDates();
    } else if (requestNumber > 1) {
      initializeFromPreviousRequest();
      setPeriodStart(getDefaultPeriodStart());
      setPeriodEnd(getDefaultPeriodEnd());
    } else {
      setBaseLines([DEFAULT_LINE()]);
      setExtraLines([]);
      setStatus('draft');
      setPeriodStart(getDefaultPeriodStart());
      setPeriodEnd(getDefaultPeriodEnd());
    }
    setPeriodError('');
  }, [isOpen, requestId, requestNumber]);

  const loadPeriodDates = async () => {
    try {
      const req = await paymentRequestService.getById(requestId);
      if (req?.period_start) {
        setPeriodStart(req.period_start.slice(0, 10));
      } else {
        setPeriodStart(getDefaultPeriodStart());
      }
      if (req?.period_end) {
        setPeriodEnd(req.period_end.slice(0, 10));
      } else {
        setPeriodEnd(getDefaultPeriodEnd());
      }
      // Load applyHoldback from stored retention_rate
      const storedRate = req?.retention_rate;
      setApplyHoldback(storedRate == null ? true : storedRate > 0);
    } catch (err) {
      console.error('Error loading period dates:', err);
      setPeriodStart(getDefaultPeriodStart());
      setPeriodEnd(getDefaultPeriodEnd());
    }
  };

  const getLastRequest = async (projectId) => {
    const { data, error } = await supabase
      ?.from('payment_requests')
      ?.select('*')
      ?.eq('project_id', projectId)
      ?.order('number', { ascending: false })
      ?.limit(1)
      ?.single();
    if (error) throw error;
    return data;
  };

  const initializeFromPreviousRequest = async () => {
    setLoading(true);
    try {
      const items = await paymentRequestService.getItemsByProjectId(projectId);
      const previousRequest = await getLastRequest(projectId);
      const previousValues = await paymentRequestService.getValuesByRequestId(previousRequest.id);

      const mappedLines = items.map(item => {
        const prev = previousValues.find(v => v.item_id === item.id);
        const prevAmt = parseFloat(prev?.current_amount) || 0;
        const contractAmt = parseFloat(item.contract_amount) || 0;
        // payment_items ne stocke pas le type : un montant negatif EST un credit.
        const isCredit = contractAmt < 0;
        const currentPct = contractAmt !== 0 ? (prevAmt / contractAmt) * 100 : 0;
        return {
          ...DEFAULT_LINE(),
          id: item.id,
          description: item.description,
          contractAmount: contractAmt,
          previousAmount: prevAmt,
          currentAmount: prevAmt,
          monthlyAmount: 0,
          percentInput: formatPercentage(currentPct),
          dollarInput: prevAmt !== 0 ? prevAmt.toFixed(2) : '',
          isExtra: item.is_extra,
          isCredit,
          linkedExtraId: item.linked_extra_id || null,
        };
      });

      setBaseLines(mappedLines.filter(l => !l.isExtra).length > 0
        ? mappedLines.filter(l => !l.isExtra)
        : [DEFAULT_LINE()]);
      setExtraLines(mappedLines.filter(l => l.isExtra));
      setStatus('draft');
    } catch (err) {
      console.error('Error initializing from previous request:', err);
      setBaseLines([DEFAULT_LINE()]);
      setExtraLines([]);
      setStatus('draft');
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const { baseItems, extraItems } = await paymentRequestService.loadRequestData(
        projectId,
        requestId,
        requestNumber
      );

      const toLine = (item) => {
        const pct = item.contractAmount !== 0
          ? formatPercentage((item.currentAmount / item.contractAmount) * 100)
          : '';
        return {
          ...DEFAULT_LINE(),
          id: item.id,
          description: item.description,
          contractAmount: item.contractAmount,
          isExtra: item.isExtra,
          isCredit: item.contractAmount < 0,
          previousAmount: item.previousAmount,
          currentAmount: item.currentAmount,
          monthlyAmount: item.monthlyAmount,
          percentInput: pct,
          dollarInput: item.currentAmount !== 0 ? String(item.currentAmount) : '',
          linkedExtraId: item.linkedExtraId || null,
        };
      };

      setBaseLines(baseItems.length > 0 ? baseItems.map(toLine) : [DEFAULT_LINE()]);
      setExtraLines(extraItems.map(toLine));
    } catch (err) {
      console.error('Error loading payment request data:', err);
    } finally {
      setLoading(false);
    }
  };

  // ─── Document helpers ────────────────────────────────────────────────────────
  const fetchDocFiles = useCallback(async () => {
    if (!storagePath) return;
    setLoadingDocFiles(true);
    try {
      const { data, error } = await supabase?.storage
        ?.from('contracts-documents')
        ?.list(storagePath, { limit: 100, sortBy: { column: 'name', order: 'asc' } });
      if (!error) {
        setDocFiles((data || []).filter(f => f?.name && f?.name !== '.emptyFolderPlaceholder'));
      }
    } catch (err) {
      console.error('Error fetching doc files:', err);
    } finally {
      setLoadingDocFiles(false);
    }
  }, [storagePath]);

  useEffect(() => {
    if (isOpen && storagePath) fetchDocFiles();
  }, [isOpen, storagePath, fetchDocFiles]);

  const sanitizeFilename = (name) =>
    name?.normalize('NFD')?.replace(/[\u0300-\u036f]/g, '')?.toLowerCase()
      ?.replace(/\s+/g, '_')?.replace(/[^a-z0-9_.]/g, '')?.replace(/_+/g, '_');

  const uploadFiles = async (fileList) => {
    if (!fileList?.length) return;
    if (!storagePath) {
      setUploadError(t('paymentRequests.errors.saveBeforeAttach'));
      return;
    }
    setUploadError(null);
    setUploading(true);
    const MAX_SIZE = 20 * 1024 * 1024;
    const errors = [];
    for (const file of Array.from(fileList)) {
      if (file?.size > MAX_SIZE) { errors.push(`${file?.name}: File too large (max 20MB)`); continue; }
      const sanitizedName = sanitizeFilename(file?.name);
      const filePath = `${storagePath}/${sanitizedName}`;
      try {
        const { error: uploadErr } = await supabase?.storage
          ?.from('contracts-documents')
          ?.upload(filePath, file, { upsert: true, cacheControl: '3600' });
        if (uploadErr) errors.push(`${file?.name}: ${uploadErr?.message}`);
      } catch { errors.push(`${file?.name}: Unexpected error`); }
    }
    if (errors.length) setUploadError(errors.join('\n'));
    setUploading(false);
    await fetchDocFiles();
  };

  const deleteDocFile = async (fileName) => {
    setDeletingFile(fileName);
    const filePath = `${storagePath}/${fileName}`;
    try {
      const { error } = await supabase?.storage?.from('contracts-documents')?.remove([filePath]);
      if (!error) setDocFiles(prev => prev.filter(f => f?.name !== fileName));
    } catch { /* ignore */ }
    setDeletingFile(null);
  };

  const getFileTypeIcon = (fileName) => {
    const ext = fileName?.split('.')?.pop()?.toLowerCase();
    if (ext === 'pdf') return <Icon name="FileText" size={14} className="text-red-500 flex-shrink-0" />;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return <Icon name="FileSpreadsheet" size={14} className="text-green-600 flex-shrink-0" />;
    if (['doc', 'docx'].includes(ext)) return <Icon name="FileText" size={14} className="text-blue-500 flex-shrink-0" />;
    return <Icon name="File" size={14} className="text-gray-400 flex-shrink-0" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // ─── Line helpers ────────────────────────────────────────────────────────────
  const updateLine = (lines, setLines, index, field, value) => {
    setLines(prev => {
      const updated = [...prev];
      const line = { ...updated[index], errors: { ...updated[index].errors } };

      if (field === 'description') {
        line.description = value;
        if (value.trim()) delete line.errors.description;
      }

      if (field === 'contractAmount') {
        const num = parseFloat(value) || 0;
        line.contractAmount = num;
        if (line.percentInput !== '') {
          const pct = parseFloat(line.percentInput) || 0;
          // currentAmount = cumulative value to date
          line.currentAmount = floorAtZero(line, (num * pct) / 100);
          line.dollarInput = line.currentAmount.toFixed(2);
          line.monthlyAmount = line.currentAmount - (line.previousAmount || 0);
        }
      }

      if (field === 'percentInput') {
        line.percentInput = value;
        const pct = parseFloat(value) || 0;
        const previousPct = line.contractAmount > 0
          ? ((line.previousAmount || 0) / line.contractAmount) * 100
          : 0;
        // currentAmount = cumulative value to date (contractAmount × currentPercentage)
        const current = (line.contractAmount * pct) / 100;
        line.currentAmount = floorAtZero(line, current);
        line.dollarInput = line.currentAmount !== 0 ? line.currentAmount.toFixed(2) : '';
        // monthlyAmount = this request only (delta)
        line.monthlyAmount = line.currentAmount - (line.previousAmount || 0);
        delete line.errors.currentAmount;
        if (pct < previousPct) {
          line.errors.currentAmount = `Current % (${formatPercentage(pct)}%) cannot be less than previous % (${formatPercentage(previousPct)}%)`;
        } else if (pct > 100) {
          line.errors.currentAmount = 'Current % cannot exceed 100%';
        }
      }

      if (field === 'dollarInput') {
        line.dollarInput = value;
        const dollar = parseFloat(value) || 0;
        // dollarInput represents cumulative value to date
        line.currentAmount = floorAtZero(line, dollar);
        if (line.contractAmount !== 0) {
          line.percentInput = formatPercentage((dollar / line.contractAmount) * 100);
        }
        line.monthlyAmount = line.currentAmount - (line.previousAmount || 0);
        delete line.errors.currentAmount;
      }

      if (!line.errors.currentAmount) {
        if (line.currentAmount < 0 && !line.isCredit) {
          line.errors.currentAmount = t('paymentRequests.validation.negativeValue');
        } else if (!line.isExtra && line.contractAmount > 0 && line.currentAmount > line.contractAmount) {
          line.errors.currentAmount = t('paymentRequests.validation.exceedsContract');
        }
      }

      updated[index] = line;
      return updated;
    });
  };

  // ─── Signe d'un credit ──────────────────────────────────────────────────────
  // project_extras stocke les credits en POSITIF : le signe vit dans `type`.
  // Un credit ajoute tel quel FACTURERAIT le client au lieu de le crediter.
  // -Math.abs() garantit que le signe est pose une fois et une seule, meme si
  // une ligne venait un jour a etre stockee deja negative.
  const isCreditExtra = (extra) => extra?.type === 'credit';
  const signedContractAmount = (extra) => {
    const raw = parseFloat(extra?.cost_accepted ?? extra?.amount) || 0;
    return isCreditExtra(extra) ? -Math.abs(raw) : raw;
  };

  // ─── Extra dropdown selection handler ───────────────────────────────────────
  const handleExtraSelect = (index, extraId) => {
    if (!extraId) return;
    const extra = approvedExtras?.find(e => e?.id === extraId);
    if (!extra) return;
    const contractAmt = signedContractAmount(extra);
    setExtraLines(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        linkedExtraId: extraId,
        description: extra?.title || '',
        contractAmount: contractAmt,
        isCredit: isCreditExtra(extra),
        errors: {},
      };
      return updated;
    });
  };

  const addBaseLine = () => setBaseLines(prev => [...prev, DEFAULT_LINE()]);
  const addExtraLine = () => setExtraLines(prev => [...prev, { ...DEFAULT_LINE(), isExtra: true }]);

  const removeExtraLine = async (index) => {
    const line = extraLines[index];
    if (line?.id) {
      deletedItemIds.current.add(line.id);
      try {
        await paymentRequestService.deleteItem(line.id);
      } catch (err) {
        console.error('Error deleting extra line from DB:', err);
      }
    }
    setExtraLines(prev => prev.filter((_, i) => i !== index));
  };

  const removeBaseLine = async (index) => {
    const line = baseLines[index];
    if (line?.id) {
      deletedItemIds.current.add(line.id);
      try {
        await paymentRequestService.deleteItem(line.id);
      } catch (err) {
        console.error('Error deleting base line from DB:', err);
      }
    }
    setBaseLines(prev => prev.filter((_, i) => i !== index));
  };

  // ─── Summary calculations (cumulative totals) ────────────────────────────────
  // contractTotal = sum of monthlyAmount (this request only) for all base lines
  const contractTotal = baseLines.reduce((s, l) => s + (l.monthlyAmount || 0), 0);
  // extrasTotal = sum of monthlyAmount (this request only) for all extra lines
  const extrasTotal = extraLines.reduce((s, l) => s + (l.monthlyAmount || 0), 0);
  const subtotal = contractTotal + extrasTotal;
  const holdbackRate = applyHoldback ? 10 : 0;
  const { retention, avantTaxes: taxableBase, tps, tvq, totalAvecTaxes: grandTotal } = computeDpTotals(subtotal, holdbackRate);

  // ─── Revised contract calculations (contract amounts, not monthly deltas) ────
  const totalContractAmount = baseLines.reduce((s, l) => s + (l.contractAmount || 0), 0);
  const totalExtrasContractAmount = extraLines.reduce((s, l) => s + (l.contractAmount || 0), 0);
  const revisedContractAmount = totalContractAmount + totalExtrasContractAmount;

  // ─── DP2+ Précédent / Cette demande totals ───────────────────────────────────
  const previousContractTotal = baseLines.reduce((s, l) => s + (l.previousAmount || 0), 0);
  const previousExtrasTotal   = extraLines.reduce((s, l) => s + (l.previousAmount || 0), 0);
  // "Cette demande" totals = contractTotal / extrasTotal (already computed above)

  // ─── DP2+ "Valeur à ce jour" = sum(currentAmount) cumul ─────────────────────
  const currentAmountContractTotal = baseLines.reduce((s, l) => s + (l.currentAmount || 0), 0);
  const currentAmountExtrasTotal   = extraLines.reduce((s, l) => s + (l.currentAmount || 0), 0);
  // Verify: currentAmountContractTotal + currentAmountExtrasTotal
  //       = (previousContractTotal + previousExtrasTotal) + (contractTotal + extrasTotal)

  // ─── Save ────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setGlobalError('');
    setPeriodError('');

    // Safety net: never save an approved DP
    if (existingStatus === 'approved') {
      setGlobalError(t('paymentRequests.saveBlockedApproved'));
      return;
    }

    // Validate period dates
    if (!periodStart || !periodEnd) {
      setPeriodError(t('paymentRequests.modal.periodInvalid') || 'Both period dates are required');
      return;
    }
    if (periodEnd < periodStart) {
      setPeriodError(t('paymentRequests.modal.periodInvalid') || 'End date must be after start date');
      return;
    }

    let hasErrors = false;
    const validateLines = (lines, setLines) => {
      setLines(prev => prev.map(line => {
        const errors = {};
        if (!line.description.trim()) {
          errors.description = t('paymentRequests.validation.descriptionRequired');
          hasErrors = true;
        }
        if (line.currentAmount < 0 && !line.isCredit) {
          errors.currentAmount = t('paymentRequests.validation.negativeValue');
          hasErrors = true;
        }
        if (!line.isExtra && line.contractAmount > 0 && line.currentAmount > line.contractAmount) {
          errors.currentAmount = t('paymentRequests.validation.exceedsContract');
          hasErrors = true;
        }
        return { ...line, errors };
      }));
    };
    validateLines(baseLines, setBaseLines);
    validateLines(extraLines, setExtraLines);
    if (hasErrors) return;

    setSaving(true);
    try {
      await onSave({ baseLines, extraLines, status, requestId, requestNumber, periodStart, periodEnd, retention_rate: holdbackRate });
      onClose();
    } catch (err) {
      console.error('Error saving payment request:', err);
      setGlobalError(err?.message || 'Failed to save payment request');
    } finally {
      setSaving(false);
    }
  };

  // Echap = meme action que le bouton Annuler ; inactif pendant l'enregistrement.
  useEscapeKey(onClose, !!isOpen && !saving);

  if (!isOpen) return null;

  // ─── Build extras dropdown options ──────────────────────────────────────────
  const currentlySelectedExtraIds = new Set(
    extraLines?.map(l => l?.linkedExtraId).filter(Boolean)
  );

  const getExtraDropdownOptions = (currentIndex) => {
    const currentRowExtraId = extraLines?.[currentIndex]?.linkedExtraId;
    return approvedExtras?.map(extra => {
      const usedInPrevious = usedExtraIds?.has(extra?.id) && extra?.id !== currentRowExtraId;
      const usedInCurrentModal = currentlySelectedExtraIds?.has(extra?.id) && extra?.id !== currentRowExtraId;
      const disabled = usedInPrevious || usedInCurrentModal;
      const disabledLabel = usedInPrevious ? t('paymentRequests.extras.alreadySubmitted') : null;
      return {
        value: extra?.id,
        label: extra?.title || extra?.id,
        coNumber: extra?.co_number || null,
        disabled,
        disabledLabel,
        isCredit: isCreditExtra(extra),
        creditLabel: isCreditExtra(extra) ? t('paymentRequests.extras.credit') : null,
        contractAmount: signedContractAmount(extra),
      };
    }) || [];
  };

  // ─── Accounting format helpers ───────────────────────────────────────────────
  const formatAccounting = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // ─── Shared grid template for both base and extras sections ─────────────────
  // DP1: Description | Contract | % | Value | delete
  // DP2: Description | Contract | Curr% | ValueToDate | Prev% | Prev$ | ThisReq | delete
  const gridTemplate = isDP1
    ? '2fr 0.9fr 3rem 0.9fr auto' :'2fr 0.9fr 3rem 0.9fr 3rem 0.9fr 0.9fr auto';

  const gridClass = isDP1
    ? 'grid grid-cols-[2.5fr_1.2fr_0.7fr_1.2fr_0.4fr] gap-2 items-center'
    : 'grid grid-cols-[2.5fr_1.2fr_0.7fr_1.2fr_0.7fr_1.2fr_1.2fr_0.4fr] gap-2 items-center';

  const renderLineRow = (line, index, isExtra, lines, setLines) => {
    const canEditContract = isDP1;
    const isLastBaseLine = !isExtra && baseLines.length <= 1;

    // DP1: all fields editable. DP2+: only currentPercentage editable
    const isFirstRequest = isDP1;

    const previousPct = line.contractAmount !== 0
      ? ((line.previousAmount || 0) / line.contractAmount) * 100
      : 0;

    const handleContractBlur = () => {
      if (isExtra && line?.linkedExtraId) return;
      if (!canEditContract) return;
      const num = parseFloat(String(line.contractRawInput).replace(/,/g, '')) || 0;
      setLines(prev => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          isEditingContractAmount: false,
          contractRawInput: '',
        };
        return updated;
      });
      updateLine(lines, setLines, index, 'contractAmount', num);
    };

    const contractDisplayValue = line.isEditingContractAmount
      ? line.contractRawInput
      : (line.contractAmount !== 0 ? formatAccounting(line.contractAmount) : '');

    const dollarDisplayValue = line.dollarFocused
      ? line.dollarInput
      : (line.currentAmount !== 0 ? formatAccounting(line.currentAmount) : '');

    // % display: show formatted (no trailing zeros) when not focused
    const pctDisplayValue = line.percentFocused
      ? line.percentInput
      : (line.percentInput !== '' ? formatPercentage(parseFloat(line.percentInput) || 0) : '');

    // Disabled field styles
    const disabledInputClass = 'bg-gray-100 text-gray-500 cursor-not-allowed border-gray-200';
    const enabledInputClass = 'bg-white';

    return (
      <div key={index} className={`${gridClass} mb-1.5`}>
        {/* Description */}
        <div className="min-w-0">
          {isExtra ? (
            <div className="relative w-full min-w-0">
              <ExtraSelectDropdown
                options={getExtraDropdownOptions(index)}
                value={line?.linkedExtraId || ''}
                onChange={(extraId) => handleExtraSelect(index, extraId)}
                placeholder={t('paymentRequests.extras.selectExtra')}
                hasError={!!line?.errors?.description}
                disabled={!isFirstRequest}
              />
              {line?.errors?.description && <p className="text-[10px] text-red-500 mt-0.5">{line.errors.description}</p>}
            </div>
          ) : (
            <div>
              <input
                type="text"
                value={line.description}
                onChange={e => isFirstRequest && updateLine(lines, setLines, index, 'description', e.target.value)}
                placeholder={t('paymentRequests.modal.description')}
                disabled={!isFirstRequest}
                className={`w-full h-9 px-3 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 ${!isFirstRequest ? disabledInputClass : enabledInputClass} ${line.errors?.description ? 'border-red-400' : 'border-gray-200'}`}
              />
              {line.errors?.description && <p className="text-[10px] text-red-500 mt-0.5">{line.errors.description}</p>}
            </div>
          )}
        </div>

        {/* Contract Amount */}
        <div>
          <AmountInput
            value={contractDisplayValue}
            onChange={e => {
              if (!isFirstRequest) return;
              if (isExtra && !!line?.linkedExtraId) return;
              setLines(prev => {
                const updated = [...prev];
                updated[index] = {
                  ...updated[index],
                  contractRawInput: e.target.value,
                };
                return updated;
              });
            }}
            onFocus={(e) => {
              if (!isFirstRequest || (isExtra && !!line?.linkedExtraId)) return;
              e.target.select();
              setLines(prev => {
                const updated = [...prev];
                updated[index] = {
                  ...updated[index],
                  isEditingContractAmount: true,
                  contractRawInput: line.contractAmount > 0 ? String(line.contractAmount) : '',
                };
                return updated;
              });
            }}
            onBlur={() => {
              handleContractBlur();
            }}
            readOnly={!isFirstRequest || (isExtra && !!line?.linkedExtraId)}
            className={!isFirstRequest ? disabledInputClass : ''}
          />
        </div>

        {/* Curr % (editable in DP1 and DP2+) */}
        <div>
          <div className="relative">
            {/* Ghost hint: shows previousPct behind the input in DP2 when field is empty */}
            {!isDP1 && !pctDisplayValue && previousPct > 0 && (
              <span className="absolute inset-0 flex items-center justify-center text-xs text-orange-200 font-medium pointer-events-none select-none z-0">
                {previousPct % 1 === 0 ? previousPct.toFixed(0) : previousPct.toFixed(1)}
              </span>
            )}
            <input
              type="text"
              value={pctDisplayValue}
              onChange={e => {
                const raw = e.target.value.replace(/[^0-9.]/g, '');
                updateLine(lines, setLines, index, 'percentInput', raw);
              }}
              onFocus={(e) => {
                e.target.select();
                setLines(prev => {
                  const updated = [...prev];
                  updated[index] = { ...updated[index], percentFocused: true };
                  return updated;
                });
              }}
              onBlur={() => {
                setLines(prev => {
                  const updated = [...prev];
                  updated[index] = { ...updated[index], percentFocused: false };
                  return updated;
                });
              }}
              placeholder=""
              className={`relative z-10 w-full h-9 px-1 pr-4 text-xs text-center border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 bg-transparent transition-colors
                ${line.errors?.currentAmount ? 'border-red-400 focus:ring-2 focus:ring-red-400/30 focus:border-red-400' : (!isDP1 ? 'border-orange-300 focus:ring-2 focus:ring-primary/20 focus:border-primary' : 'focus:ring-2 focus:ring-primary/20 focus:border-primary border-gray-200')}`}
            />
            <span className="absolute right-1 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none select-none z-20">%</span>
          </div>
          {line.errors?.currentAmount && <p className="text-[10px] text-red-500 mt-0.5 leading-tight">{line.errors.currentAmount}</p>}
        </div>

        {/* Value to Date ($) — always auto-calculated, never manually editable */}
        <div>
          <AmountInput
            value={dollarDisplayValue}
            readOnly
            className={disabledInputClass}
            hasError={!!line.errors?.currentAmount}
          />
        </div>

        {/* Prev % (DP2+ only, readonly) */}
        {!isDP1 && (
          <div>
            <div className="relative">
              <input
                type="text"
                value={previousPct > 0 ? formatPercentage(previousPct) : '0'}
                readOnly
                className={`w-full h-9 px-1 pr-4 text-xs text-center font-medium border border-gray-200 rounded-md ${disabledInputClass}`}
              />
              <span className="absolute right-1 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none select-none">%</span>
            </div>
          </div>
        )}

        {/* Previous $ (DP2+ only, readonly) */}
        {!isDP1 && (
          <div>
            <AmountInput
              value={line.previousAmount !== 0 ? formatAccounting(line.previousAmount) : '0.00'}
              readOnly
              className={disabledInputClass}
            />
          </div>
        )}

        {/* This Request (DP2+ only, readonly — delta/monthly) */}
        {!isDP1 && (
          <div>
            <AmountInput
              value={line.monthlyAmount !== 0 ? formatAccounting(line.monthlyAmount) : ''}
              readOnly
              className={disabledInputClass}
            />
          </div>
        )}

        {/* Delete */}
        <div className="flex items-center justify-center">
          {isExtra ? (
            <button
              type="button"
              onClick={() => removeExtraLine(index)}
              className="p-1 text-gray-400 hover:text-red-500 transition-colors"
            >
              <Icon name="Trash2" size={14} />
            </button>
          ) : !isLastBaseLine ? (
            <button
              type="button"
              onClick={() => removeBaseLine(index)}
              className="p-1 text-gray-400 hover:text-red-500 transition-colors"
            >
              <Icon name="Trash2" size={14} />
            </button>
          ) : (
            <div className="w-5" />
          )}
        </div>
      </div>
    );
  };

  const colHeaders = isDP1
    ? [t('paymentRequests.modal.description'), t('paymentRequests.modal.contractAmount'), t('paymentRequests.modal.percent'), t('paymentRequests.modal.valueToDate'), '']
    : [t('paymentRequests.modal.description'), t('paymentRequests.modal.contractAmount'), 'Curr %', t('paymentRequests.modal.valueToDate'), 'Prev %', t('paymentRequests.modal.previous'), t('paymentRequests.modal.thisRequest'), ''];

  const selectedStatusLabel = t(`paymentRequests.status.${status}`) || status;

  return (
    // Modale de formulaire : pas de fermeture au clic exterieur.
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl flex flex-col max-h-[90vh] overflow-hidden" role="dialog" aria-modal="true">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
              <Icon name="Receipt" size={18} className="text-emerald-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-900 leading-snug truncate">
                {isEditMode ? t('paymentRequests.modal.titleEdit') : t('paymentRequests.modal.titleCreate')}
                {requestNumber && <span className="ml-2 text-gray-400 font-normal">— DP{requestNumber}</span>}
              </h2>
              <p className="text-[11px] text-gray-500 truncate">
                {isDP1 ? t('paymentRequests.modal.dp1Notice') : t('paymentRequests.modal.dp2Notice')}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <div ref={statusRef} className="relative">
              <button
                type="button"
                onClick={() => setStatusOpen(prev => !prev)}
                className="flex items-center gap-1.5 h-7 px-2 text-xs font-medium border border-gray-200 rounded-md bg-white hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <span className={STATUS_COLORS[status] || 'text-gray-500'}>{selectedStatusLabel}</span>
                <Icon name="ChevronDown" size={12} className={`text-gray-400 transition-transform ${statusOpen ? 'rotate-180' : ''}`} />
              </button>
              {statusOpen && (
                <div className="absolute right-0 top-full mt-1 z-[9999] bg-white border border-gray-200 rounded-md shadow-md py-1 min-w-[120px]">
                  {STATUS_OPTIONS.map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => { setStatus(s); setStatusOpen(false); }}
                      className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${
                        s === status
                          ? `bg-gray-50 font-medium ${STATUS_ITEM_COLORS[s]}`
                          : `${STATUS_ITEM_COLORS[s]} hover:bg-gray-50`
                      }`}
                    >
                      {t(`paymentRequests.status.${s}`)}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button type="button" onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0">
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Icon name="Loader2" size={32} className="animate-spin text-primary" />
            </div>
          ) : (
            <>
              {/* ── Period Dates ── */}
              <div className="mb-5 pb-4 border-b border-gray-100">
                <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                  {t('paymentRequests.modal.periodSection') || 'Période'}
                </h3>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">
                      {t('paymentRequests.modal.periodStartLabel') || 'Date de début de période'}
                    </label>
                    <input
                      type="date"
                      value={periodStart}
                      onChange={e => { setPeriodStart(e.target.value); setPeriodError(''); }}
                      className="w-full h-9 px-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wide mb-1">
                      {t('paymentRequests.modal.periodEndLabel') || 'Date de fin de période'}
                    </label>
                    <input
                      type="date"
                      value={periodEnd}
                      onChange={e => { setPeriodEnd(e.target.value); setPeriodError(''); }}
                      className="w-full h-9 px-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
                    />
                  </div>
                </div>
                {periodError && (
                  <p className="text-xs text-red-500 mt-1.5">{periodError}</p>
                )}
              </div>

              {/* ── Base Lines ── */}
              <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                    {t('paymentRequests.modal.baseLines')}
                  </h3>
                </div>

                {/* Column headers — same grid as rows */}
                <div className={`${gridClass} mb-1`}>
                  {colHeaders.map((h, i) => (
                    <div key={i} className={`text-[10px] font-medium text-gray-400 uppercase tracking-wide px-1 ${i === 0 ? 'text-left' : i === colHeaders.length - 1 ? 'text-center' : 'text-right'}`}>{h}</div>
                  ))}
                </div>

                {baseLines.map((line, i) => renderLineRow(line, i, false, baseLines, setBaseLines))}

                {isDP1 && (
                  <button
                    type="button"
                    onClick={addBaseLine}
                    className="mt-2 text-xs text-primary hover:text-orange-600 font-medium flex items-center gap-1 transition-colors"
                  >
                    {t('paymentRequests.modal.addLine')}
                  </button>
                )}
              </div>

              {/* ── Extras ── */}
              <div className="mb-5 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                    {t('paymentRequests.modal.extrasSection')}
                  </h3>
                </div>

                {extraLines.length > 0 && (
                  <>
                    {/* Column headers — same grid as base lines for perfect alignment */}
                    <div className={`${gridClass} mb-1`}>
                      {colHeaders.map((h, i) => (
                        <div key={i} className={`text-[10px] font-medium text-gray-400 uppercase tracking-wide px-1 ${i === 0 ? 'text-left' : i === colHeaders.length - 1 ? 'text-center' : 'text-right'}`}>{h}</div>
                      ))}
                    </div>
                    {extraLines.map((line, i) => renderLineRow(line, i, true, extraLines, setExtraLines))}
                  </>
                )}

                <button
                  type="button"
                  onClick={addExtraLine}
                  className="mt-2 text-xs text-primary hover:text-orange-600 font-medium flex items-center gap-1 transition-colors"
                >
                  {t('paymentRequests.modal.addExtraLine')}
                </button>
              </div>

              {/* ── Summary ── */}
              <div className="mb-5 pt-4 border-t border-gray-100">
                <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                  {t('paymentRequests.modal.summary')}
                </h3>
                <div className="bg-gray-50 rounded-lg px-4 pr-2 py-3 space-y-1.5">

                  {/* ── DP block — two-column layout aligned under table columns ── */}
                  {/* Column header row */}
                  <div className={`${gridClass} mb-0.5`}>
                    <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide px-1 text-left col-span-1"></div>
                    <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide px-1 text-right">{t('paymentRequests.modal.contractAmount')}</div>
                    <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide px-1 text-right"></div>
                    <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide px-1 text-right">{t('paymentRequests.modal.valueToDate')}</div>
                    {!isDP1 && <div className="text-[10px] px-1"></div>}
                    {!isDP1 && <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide px-1 text-right">{t('paymentRequests.modal.previous')}</div>}
                    {!isDP1 && <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wide px-1 text-right">{t('paymentRequests.modal.thisRequest')}</div>}
                    <div className="text-[10px] px-1"></div>
                  </div>

                  {/* Total contractuel */}
                  <div className={`${gridClass}`}>
                    <div className="text-xs text-gray-600 px-1">{t('paymentRequests.modal.contractTotal')}</div>
                    <div className="text-xs font-medium text-gray-700 text-right tabular-nums px-1">{formatCurrency(totalContractAmount)}</div>
                    <div></div>
                    <div className="text-xs font-medium text-gray-700 text-right tabular-nums px-1">{isDP1 ? formatCurrency(contractTotal) : formatCurrency(currentAmountContractTotal)}</div>
                    {!isDP1 && <div></div>}
                    {!isDP1 && <div className="text-xs font-medium text-gray-700 text-right tabular-nums px-1">{formatCurrency(previousContractTotal)}</div>}
                    {!isDP1 && <div className="text-xs font-medium text-gray-700 text-right tabular-nums px-1">{formatCurrency(contractTotal)}</div>}
                    <div></div>
                  </div>

                  {/* Total extras */}
                  <div className={`${gridClass}`}>
                    <div className="text-xs text-gray-600 px-1">{t('paymentRequests.modal.extrasTotal')}</div>
                    <div className="text-xs font-medium text-gray-700 text-right tabular-nums px-1">{formatCurrency(totalExtrasContractAmount)}</div>
                    <div></div>
                    <div className="text-xs font-medium text-gray-700 text-right tabular-nums px-1">{isDP1 ? formatCurrency(extrasTotal) : formatCurrency(currentAmountExtrasTotal)}</div>
                    {!isDP1 && <div></div>}
                    {!isDP1 && <div className="text-xs font-medium text-gray-700 text-right tabular-nums px-1">{formatCurrency(previousExtrasTotal)}</div>}
                    {!isDP1 && <div className="text-xs font-medium text-gray-700 text-right tabular-nums px-1">{formatCurrency(extrasTotal)}</div>}
                    <div></div>
                  </div>

                  {/* Sous-total */}
                  <div className={`${gridClass} border-t border-gray-200 pt-1.5`}>
                    <div className="text-xs font-semibold text-gray-700 px-1">{t('paymentRequests.modal.subtotal')}</div>
                    <div className="text-xs font-semibold text-gray-700 text-right tabular-nums px-1">{formatCurrency(totalContractAmount + totalExtrasContractAmount)}</div>
                    <div></div>
                    <div className="text-xs font-semibold text-gray-700 text-right tabular-nums px-1">{isDP1 ? formatCurrency(subtotal) : formatCurrency(currentAmountContractTotal + currentAmountExtrasTotal)}</div>
                    {!isDP1 && <div></div>}
                    {!isDP1 && <div className="text-xs font-semibold text-gray-700 text-right tabular-nums px-1">{formatCurrency(previousContractTotal + previousExtrasTotal)}</div>}
                    {!isDP1 && <div className="text-xs font-semibold text-gray-700 text-right tabular-nums px-1">{formatCurrency(subtotal)}</div>}
                    <div></div>
                  </div>

                  {/* Retenue */}
                  <div className={`${gridClass}`}>
                    <div className="flex items-center gap-2 px-1">
                      <Checkbox
                        checked={applyHoldback}
                        onChange={e => setApplyHoldback(e.target.checked)}
                        id="apply-holdback-checkbox"
                        className="[&_label]:border-primary [&_label]:data-[state=checked]:bg-primary [&_label]:data-[state=checked]:border-primary"
                      />
                      <span className="text-xs text-red-500">— {t('paymentRequests.modal.retention', { rate: holdbackRate })}</span>
                    </div>
                    <div></div>
                    <div></div>
                    <div className={`text-xs text-red-500 text-right tabular-nums px-1 ${!isDP1 ? 'invisible' : ''}`}>({formatCurrency(retention)})</div>
                    {!isDP1 && <div></div>}
                    {!isDP1 && <div></div>}
                    {!isDP1 && <div className="text-xs text-red-500 text-right tabular-nums px-1">({formatCurrency(retention)})</div>}
                    <div></div>
                  </div>

                  {/* Sous-total après retenue */}
                  <div className={`${gridClass}`}>
                    <div className="text-xs text-gray-700 px-1">{t('paymentRequests.modal.subtotalAfterHoldback')}</div>
                    <div></div>
                    <div></div>
                    <div className={`text-xs text-gray-700 text-right tabular-nums px-1 ${!isDP1 ? 'invisible' : ''}`}>{formatCurrency(taxableBase)}</div>
                    {!isDP1 && <div></div>}
                    {!isDP1 && <div></div>}
                    {!isDP1 && <div className="text-xs text-gray-700 text-right tabular-nums px-1">{formatCurrency(taxableBase)}</div>}
                    <div></div>
                  </div>

                  {/* TPS */}
                  <div className={`${gridClass}`}>
                    <div className="text-xs text-gray-600 px-1">+ {t('paymentRequests.modal.tps')}</div>
                    <div></div>
                    <div></div>
                    <div className={`text-xs text-gray-600 text-right tabular-nums px-1 ${!isDP1 ? 'invisible' : ''}`}>{formatCurrency(tps)}</div>
                    {!isDP1 && <div></div>}
                    {!isDP1 && <div></div>}
                    {!isDP1 && <div className="text-xs text-gray-600 text-right tabular-nums px-1">{formatCurrency(tps)}</div>}
                    <div></div>
                  </div>

                  {/* TVQ */}
                  <div className={`${gridClass}`}>
                    <div className="text-xs text-gray-600 px-1">+ {t('paymentRequests.modal.tvq')}</div>
                    <div></div>
                    <div></div>
                    <div className={`text-xs text-gray-600 text-right tabular-nums px-1 ${!isDP1 ? 'invisible' : ''}`}>{formatCurrency(tvq)}</div>
                    {!isDP1 && <div></div>}
                    {!isDP1 && <div></div>}
                    {!isDP1 && <div className="text-xs text-gray-600 text-right tabular-nums px-1">{formatCurrency(tvq)}</div>}
                    <div></div>
                  </div>

                  {/* Total */}
                  <div className={`${gridClass} border-t border-gray-200 pt-1.5`}>
                    <div className="text-sm font-semibold text-gray-900 px-1">{t('paymentRequests.modal.total')}</div>
                    <div></div>
                    <div></div>
                    <div className={`text-sm font-semibold text-primary text-right tabular-nums px-1 ${!isDP1 ? 'invisible' : ''}`}>{formatCurrency(grandTotal)}</div>
                    {!isDP1 && <div></div>}
                    {!isDP1 && <div></div>}
                    {!isDP1 && <div className="text-sm font-semibold text-primary text-right tabular-nums px-1">{formatCurrency(grandTotal)}</div>}
                    <div></div>
                  </div>

                </div>
              </div>

              {/* ── Documents ── */}
              <div className="mb-2 pt-4 border-t border-gray-100">
                <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  {t('paymentRequests.modal.documents')}
                </h3>

                {storagePath && (
                  <>
                    {loadingDocFiles ? (
                      <div className="flex items-center gap-2 py-2 mb-2">
                        <Icon name="Loader2" size={14} className="animate-spin text-primary" />
                        <span className="text-xs text-gray-400">Loading...</span>
                      </div>
                    ) : docFiles.length > 0 ? (
                      <ul className="space-y-1 mb-3">
                        {docFiles.map(file => (
                          <li key={file?.name} className="group flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50">
                            {getFileTypeIcon(file?.name)}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-700 truncate">{file?.name}</p>
                              {file?.metadata?.size && <p className="text-[10px] text-gray-400">{formatFileSize(file?.metadata?.size)}</p>}
                            </div>
                            <button
                              onClick={() => deleteDocFile(file?.name)}
                              disabled={deletingFile === file?.name}
                              className="p-1 text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                            >
                              {deletingFile === file?.name
                                ? <Icon name="Loader2" size={12} className="animate-spin" />
                                : <Icon name="Trash2" size={12} />}
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </>
                )}

                {uploadError && (
                  <div className="mb-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 whitespace-pre-line">{uploadError}</div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.xlsx,.xls,.csv,.doc,.docx"
                  className="hidden"
                  onChange={e => { uploadFiles(e?.target?.files); if (fileInputRef?.current) fileInputRef.current.value = ''; }}
                />
                <div
                  onDrop={e => { e.preventDefault(); setIsDragOver(false); uploadFiles(e?.dataTransfer?.files); }}
                  onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
                  onDragEnter={e => { e.preventDefault(); setIsDragOver(true); }}
                  onDragLeave={() => setIsDragOver(false)}
                  onClick={() => fileInputRef?.current?.click()}
                  className={`border-2 border-dashed rounded-xl py-8 text-center cursor-pointer transition-colors ${isDragOver ? 'border-primary bg-orange-50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}`}
                >
                  {uploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Icon name="Loader2" size={22} className="animate-spin text-primary" />
                      <span className="text-xs text-gray-500">Uploading...</span>
                    </div>
                  ) : (
                    <>
                      <Icon name="Upload" size={22} className="mx-auto mb-2 text-gray-300" />
                      <p className="text-sm text-gray-500">
                        {t('paymentRequests.modal.dropFilesHere')}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">PDF, Excel, Word • Max 20MB</p>
                      {!storagePath && (
                        <p className="text-[10px] text-gray-300 mt-1 italic">Save the request first to attach files</p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-gray-100 flex-shrink-0 bg-white">
          {globalError ? (
            <p className="text-xs text-red-500">{globalError}</p>
          ) : <div />}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {t('paymentRequests.modal.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || loading}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-60"
            >
              {saving ? <Icon name="Loader2" size={13} className="animate-spin" /> : <Icon name="Check" size={13} />}
              {saving ? t('paymentRequests.modal.saving') : t('paymentRequests.modal.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── ExtraSelectDropdown ─────────────────────────────────────────────────────
const ExtraSelectDropdown = ({ options, value, onChange, placeholder, hasError }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref?.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selected = options?.find(o => o?.value === value);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className={`w-full min-w-0 h-9 flex items-center justify-between px-2 text-xs border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors ${hasError ? 'border-red-400' : 'border-gray-200'} ${open ? 'ring-1 ring-primary border-primary' : 'hover:border-gray-300'}`}
        title={selected ? `${selected.coNumber ? selected.coNumber + ' ' : ''}${selected.label}` : ''}
      >
        {selected ? (
          <span className="flex items-center gap-1.5 min-w-0 overflow-hidden flex-1">
            {selected.creditLabel && (
              <span className="flex-shrink-0 bg-red-100 text-red-600 text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">
                {selected.creditLabel}
              </span>
            )}
            {selected.coNumber && (
              <span className="flex-shrink-0 bg-orange-100 text-orange-600 text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">
                {selected.coNumber}
              </span>
            )}
            <span className="text-gray-800 truncate text-xs min-w-0">{selected.label}</span>
          </span>
        ) : (
          <span className="text-gray-400 truncate flex-1">{placeholder}</span>
        )}
        <svg className={`w-3 h-3 ml-1 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-[9999] left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-md max-h-48 overflow-y-auto">
          {options?.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-400 italic">—</div>
          ) : (
            options?.map(opt => (
              <button
                key={opt?.value}
                type="button"
                disabled={opt?.disabled}
                onClick={() => {
                  if (!opt?.disabled) { onChange(opt?.value); setOpen(false); }
                }}
                className={`w-full text-left px-3 py-2 text-xs transition-colors flex flex-col gap-0.5
                  ${opt?.disabled
                    ? 'text-gray-300 cursor-not-allowed bg-white'
                    : opt?.value === value
                      ? 'bg-orange-50 text-primary font-medium'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
              >
                <span className="flex items-center gap-1.5">
                  {opt?.creditLabel && (
                    <span className={`flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${opt?.disabled ? 'bg-gray-100 text-gray-300' : 'bg-red-100 text-red-600'}`}>
                      {opt?.creditLabel}
                    </span>
                  )}
                  {opt?.coNumber && (
                    <span className={`flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${opt?.disabled ? 'bg-gray-100 text-gray-300' : 'bg-orange-100 text-orange-600'}`}>
                      {opt?.coNumber}
                    </span>
                  )}
                  <span className="truncate">{opt?.label}</span>
                </span>
                {opt?.disabled && opt?.disabledLabel && (
                  <span className="text-[10px] text-gray-300 italic pl-0.5">{opt?.disabledLabel}</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentRequestModal;
